import http from "http";
import https from "https";

import { type Context } from "@nuxt/types";
import { type NuxtAxiosInstance } from "@nuxtjs/axios";
import { type useContext } from "@nuxtjs/composition-api";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";

import { type ApiError, type ApiObject } from "@/types";
import { $config } from "@/utils/accessors/config";
import { BAD_REQUEST, FORBIDDEN, INTERNAL_SERVER_ERROR } from "@/utils/enums/http-codes";
import { time } from "@/utils/enums/time";
import logger, { logLevel } from "@/utils/logger";

/**
 * Expose internal handlers from the interceptor manager
 */
declare module "axios" {
  interface AxiosInterceptorPair<V> {
    fulfilled?: <T = V>(value: V) => T | Promise<T>;
    rejected?: (error: unknown) => unknown;
  }

  export interface AxiosInterceptorManager<V> {
    handlers: AxiosInterceptorPair<V>[];
  }
}

// Cap maximum concurrent connections to any given host at 50. Any extra requests get queued
const maxSockets = 50;
// Max incoming request time is 30s, so it's silly to have outbound requests that last longer
const timeout = time.THIRTY_SECONDS;
// Default of LIFO means that a queued up request may never resolve when under sufficient load
const scheduling = "fifo";

const httpAgent = new http.Agent({ keepAlive: true, maxSockets, timeout, scheduling });
const httpsAgent = new https.Agent({ keepAlive: true, maxSockets, timeout, scheduling });

http.globalAgent = httpAgent;
https.globalAgent = httpsAgent;

export default class Api {
  private client: NuxtAxiosInstance;
  readonly url: string = "/";

  constructor(name: ApiGatewayServices, $axios: Context["$axios"]) {
    const serviceConfig = $config.apiGateway.services[name];
    const protocol = $config.apiGateway.protocol ?? "http";
    const hostname = serviceConfig.host || $config.apiGateway.host || "localhost";
    const baseURL = `${protocol}://${hostname}${serviceConfig.path}`;

    const client = $axios.create({
      baseURL,
      adapter: process.client ? axios.defaults.adapter : undefined,
      httpAgent: process.server ? httpAgent : undefined,
      httpsAgent: process.server ? httpsAgent : undefined,
    });

    // Inherit global interceptors: https://github.com/axios/axios/issues/993#issuecomment-607134806
    $axios.interceptors.request.handlers
      .filter(Boolean)
      .forEach(({ fulfilled, rejected }) => client.interceptors.request.use(fulfilled, rejected));
    $axios.interceptors.response.handlers
      .filter(Boolean)
      .forEach(({ fulfilled, rejected }) => client.interceptors.response.use(fulfilled, rejected));

    this.client = client;
  }

  /**
   * Makes a request using the configured axios client. URL is optional as it should be
   * specified on the inheriting class's `url` property. All other options provided will
   * be used as overrides for conventional defaults.
   */
  request<T>(options: AxiosRequestConfig) {
    const requestOptions: AxiosRequestConfig = {
      url: this.url,
      ...options,
    };

    return this.client.request<T>(requestOptions).catch((error) => {
      // Only retry 403 as other interceptors should have refreshed auth tokens.
      if (error?.response?.status === FORBIDDEN) {
        logger(logLevel.DEBUG, "Retrying API request");

        return this.client.request<T>(requestOptions);
      }

      throw error;
    });
  }
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return Boolean(
    typeof error === "object" &&
      error !== null &&
      //
      "response" in error &&
      typeof error.response === "object" &&
      error.response !== null &&
      //
      "data" in error.response &&
      typeof error.response.data === "object" &&
      error.response.data !== null &&
      "status" in error.response.data &&
      "error_code" in error.response.data &&
      isClientError((error as AxiosError).response?.status ?? 0),
  );
}

export function isClientError(statusCode: number): boolean {
  return statusCode >= BAD_REQUEST && statusCode < INTERNAL_SERVER_ERROR;
}

export function formatErrorMessage(error: unknown) {
  if (isApiError(error) && typeof error.response === "object") {
    return `
    Status: ${error.response.data.status},
    Code: ${error.response.data.error_code},
    Message: ${error.message},
    Developer Message: ${error.response.data.developer_message},
    Stack: ${error.stack}
    `;
  } else {
    return error;
  }
}

export function handleServiceError({
  context,
  error,
  originalError,
  meta,
}: {
  context: Context | ReturnType<typeof useContext>;
  error: {
    status: number;
    errorCode: string;
    developerMessage: string;
  };
  originalError?: unknown;
  meta?: { [key: string]: unknown };
}) {
  if (isApiError(error)) {
    // Ignore 4xx errors from api as bad user input
    return;
  }

  logger(
    logLevel.ERROR,
    "Service Error",
    {
      statusCode: error.status,
      code: error.errorCode,
      developerMessage: error.developerMessage,
      ...meta,
    },
    originalError,
  );

  if (context.res) {
    context.res.statusCode = error.status;
  }

  context.error({
    statusCode: error.status,
    message: `${error.developerMessage} - ${formatErrorMessage(originalError)}`,
  });
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useApi
 *
 * @desc        A utility func, which takes in a name and url param, that returns an object.
 *              containing a request function and a url writableComputed
 *              properties for configuringing the request to the API Gateway. The
 *              request function is a wrapper around the $axios plugin's request
 *              function where we then add our own logic for handling 403 errors.
 *
 * @summary     A util func used to return a base construct of an API service.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @param {string} name - The API Service name which maps to {@link ApiGatewayServices}
 * @param {string} url - The url to be used for the request
 * @param {NuxtAxiosInstance} $axios - The $axios instance to be used for the request
 *
 * @returns {ApiObject} Object for interacting with API Gateway
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Basic usage of `useApi` for settings Api</caption>
 * ```ts
 * export const useSettingsApi = () => {
 *   const _api = useApi({name:"consumerProfileService", url: `/v2/settings${process.client ? "/" : ""}`});
 *
 *   const get = async () => await _api.request<SettingsResponse>({method: "GET"});
 *
 *   return { get };
 * }
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export function useApi({
  name,
  url,
  $axios,
}: {
  name: ApiGatewayServices;
  url: string;
  $axios: NuxtAxiosInstance;
}): ApiObject {
  // Internal State

  const serviceConfig = $config.apiGateway.services[name];
  const protocol = serviceConfig.protocol ?? $config.apiGateway.protocol ?? "http";
  const hostname = serviceConfig.host || $config.apiGateway.host || "localhost";
  const baseURL = `${protocol}://${hostname}${serviceConfig.path}`;

  const client = $axios.create({
    baseURL,
    adapter: process.client ? axios.defaults.adapter : undefined,
    httpAgent: process.server ? httpAgent : undefined,
    httpsAgent: process.server ? httpsAgent : undefined,
  });

  //  Interceptors
  $axios.interceptors.request.handlers
    .filter(Boolean)
    .forEach(({ fulfilled, rejected }) => client.interceptors.request.use(fulfilled, rejected));

  $axios.interceptors.response.handlers
    .filter(Boolean)
    .forEach(({ fulfilled, rejected }) => client.interceptors.response.use(fulfilled, rejected));

  // Actions
  /**
   * Makes a request using the configured axios client. All other options provided will
   * be used as overrides for conventional defaults.
   */
  async function request<T>(options: AxiosRequestConfig) {
    const requestOptions = {
      url,
      ...options,
    };

    return await client.request<T>(requestOptions).catch((error) => {
      // Only retry 403 as other interceptors should have refreshed auth tokens.
      if (error?.response?.status === FORBIDDEN) {
        logger(logLevel.DEBUG, "Retrying API request");

        return client.request<T>(requestOptions);
      }

      throw error;
    });
  }

  return { request, url };
}

export function handleErrorStatusCode(error: unknown): number {
  const axiosErrorStatus = axios.isAxiosError(error) && error.response?.status;
  return axiosErrorStatus || INTERNAL_SERVER_ERROR;
}
